import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, ImagePlaceholder } from "@/components/site/SiteShell";
import { PageHero, Section, FeatureGrid, CTA, Breadcrumbs, Prose, FAQGrid } from "@/components/site/Sections";

const TITLE = "Karriere, bliv en del af Elektriker-Elektriker";
const DESCRIPTION =
  "Vi søger dygtige elektrikere, lærlinge og servicemontører til vores team i København. Læs om kulturen, fordelene og de ledige stillinger.";

export const Route = createFileRoute("/careers")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "/careers" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/careers" }],
  }),
  component: () => (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Karriere" }]} />
      <PageHero
        variant="media"
        accentColor="oklch(0.7 0.14 145)"
        eyebrow="Karriere · Vi søger kolleger"
        title="Bliv en del af holdet"
        intro="Vi vokser og søger løbende dygtige håndværkere, der vil arbejde i en moderne elinstallatørvirksomhed med fokus på kvalitet, faglig stolthed og en grøn dagsorden, uden de klassiske branche-uvaner."
        imageLabel="Vores team på arbejde"
      />

      <Section>
        <Prose>
          <h2>Hvorfor arbejde hos os?</h2>
          <p>
            Vi tror på at den bedste elektrikervirksomhed er den, hvor svenden har frihed under
            ansvar, har sine egne kunder, og bliver behandlet som det fagperson han eller hun er.
            Vi tror også på at en god arbejdsplads betyder ordentlige biler, godt værktøj, fair
            vagtordning og en kultur, hvor det er okay at spørge en kollega til råds.
          </p>
          <p>
            Vi er drevet af elektrikere, ikke af en administrationschef i et hjørnekontor. Det
            betyder hurtige beslutninger, færre mellemled, og at den der står med opgaven, også
            har mandatet til at løse den.
          </p>
        </Prose>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">Aktuelle stillingstyper</h2>
        <div className="mt-8">
          <FeatureGrid items={[
            { title: "Servicemontør", body: "Selvstændige servicebesøg hos private og erhverv i Storkøbenhavn. Din egen bil og dine egne kunder, men aldrig alene om beslutningerne." },
            { title: "Projektelektriker", body: "Renoverings- og nybyggeriopgaver fra A til Z, fra besigtigelse til aflevering. Ansvar for økonomi, tidsplan og kvalitet på dit projekt." },
            { title: "Lærling", body: "Få en alsidig læretid med både service og projekt. Vi tager imod nye lærlinge hvert år og giver dig den rigtige svend som mester." },
            { title: "Servicekoordinator", body: "Bindeled mellem kunder, svende og leverandører. Du bygger den daglige drift sammen med vores planlæggere." },
            { title: "Tekniker, varmepumpe", body: "Specialist i installation og service af luft-til-vand og luft-til-luft varmepumper. Du arbejder selvstændigt med VVS-koordinering." },
            { title: "Tekniker, ladestander", body: "Installation af ladebokse og ladeparker hos private, foreninger og erhverv. Bredt teknologisk spektrum og masser af nybyg." },
          ]} />
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <ImagePlaceholder label="Foto: kolleger" ratio="aspect-[5/4]" />
          <div>
            <h2 className="text-3xl">Det vi tilbyder</h2>
            <ul className="mt-5 space-y-2 text-[var(--ink)]/85">
              <li>✓ Konkurrencedygtig løn og pension</li>
              <li>✓ Frihed under ansvar, dine egne kunder</li>
              <li>✓ Nyt værktøj og moderne servicebiler (elbiler)</li>
              <li>✓ Sundhedsforsikring og personalegoder</li>
              <li>✓ Faglig udvikling, kurser og certificeringer</li>
              <li>✓ Et stærkt kollegaskab, vi mødes også udenfor arbejde</li>
              <li>✓ Fair vagtordning med ordentlig betaling</li>
              <li>✓ Mulighed for at vokse internt, flere af vores projektledere startede som lærlinge</li>
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/kontakt" className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95">Send en uopfordret ansøgning</Link>
              <a href="mailto:el@elektriker-elektriker.dk" className="inline-flex h-11 items-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">el@elektriker-elektriker.dk</a>
            </div>
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">Ofte stillede spørgsmål</h2>
        <div className="mt-8">
          <FAQGrid items={[
            { q: "Tager I imod uopfordrede ansøgninger?", a: "Ja, altid. Selv hvis vi ikke har noget aktuelt opslået, mødes vi gerne med dygtige folk, vi vokser løbende." },
            { q: "Tager I lærlinge?", a: "Ja, hvert år. Send din ansøgning med karakterer fra grundforløb, og fortæl hvad du gerne vil arbejde med." },
            { q: "Kan I tilbyde fleksible arbejdstider?", a: "Ja, vi har både fuld tid, deltid og varierende skiftehold til vagtordning. Vi taler om hvad der passer dig." },
            { q: "Hvad med løn?", a: "Vi forhandler altid løn individuelt baseret på erfaring og kompetencer. Vi ligger over overenskomsten på de fleste stillinger." },
          ]} />
        </div>
      </Section>

      <CTA title="Spørgsmål til stillingerne?" body="Skriv eller ring, vi sidder klar og fortæller gerne mere om kulturen og opgaverne." />
      <div className="h-10" />
    </SiteShell>
  ),
});
